import { useCallback } from "react";
import type { Sale } from "@/types";
import type { StoreCtx } from "@/store/appTypes";
import {
  advisorRegisterSale,
  loadAdvisorRuntime,
  saveNotification,
  saveSale,
  type AdvisorSalePayload,
} from "@/store/api";

// Acciones de ventas: registro directo (admin) y venta del asesor vía RPC seguro.
export const useSaleActions = (ctx: StoreCtx) => {
  const { data, setData, setError } = ctx;

  const addSale = useCallback(
    (s: Sale): Promise<Sale | null> =>
      (async () => {
        try {
          const saved = await saveSale(s);
          await saveNotification({
            audience: "admin",
            title: s.advisorId === "admin" ? "Nueva venta registrada" : "Venta chuleada por asesor",
            message: `${s.productName} · ${s.qty} und · $${s.total.toLocaleString("es-CO")}`,
            kind: "sale",
            date: s.date,
            read: false,
            link: s.advisorId === "admin" ? "/admin/ventas" : "/admin/consignaciones",
          });
          setData((p) => ({ ...p, sales: [saved, ...p.sales] }));
          setError(null);
          return saved;
        } catch (e) {
          setError(e instanceof Error ? e.message : "No se pudo registrar la venta.");
          return null;
        }
      })(),
    [setData, setError]
  );

  // El asesor registra su venta vía RPC seguro y luego recargamos su runtime.
  const registerAdvisorSale = useCallback(
    (payload: AdvisorSalePayload): Promise<boolean> =>
      (async () => {
        try {
          await advisorRegisterSale(payload);
          const runtime = await loadAdvisorRuntime(data.distributors.map((d) => d.id));
          setData((prev) => ({ ...prev, ...runtime }));
          setError(null);
          return true;
        } catch (e) {
          setError(e instanceof Error ? e.message : "No se pudo registrar la venta.");
          return false;
        }
      })(),
    [data.distributors, setData, setError]
  );

  return { addSale, registerAdvisorSale };
};